import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  CardContent,
  Typography,
  Chip,
  Button,
  Alert,
  useTheme,
} from '@mui/material';
import { ThumbUp as ThumbUpIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * InsightSummaryCard Component
 * Displays the insight summary with markdown support for progression insights
 */
const InsightSummaryCard = ({
  batch,
  batches,
  isProgression,
  onFeedbackClick,
  onViewPreviousReports,
}) => {
  const theme = useTheme();
  if (!batch?.summary) {
    if (batches.length > 0) {
      return (
        <CardContent sx={{ p: 2.5 }}>
          <Alert severity="info">
            No summary available for the latest{' '}
            {isProgression ? 'template progression' : 'operational'} insights
            batch.
          </Alert>
        </CardContent>
      );
    }
    return (
      <CardContent sx={{ p: 2.5 }}>
        <Alert severity="info">
          No {isProgression ? 'template progression' : 'operational'} insights
          batches available.
        </Alert>
      </CardContent>
    );
  }

  return (
    <CardContent sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            backgroundColor: theme.palette.background.default,
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src="/llama-small.png"
            alt="Llama Agent"
            sx={{
              width: 24,
              height: 24,
              objectFit: 'contain',
            }}
          />
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 600,
            flex: 1,
            fontSize: '1rem',
            letterSpacing: '-0.01em',
          }}
        >
          Summary by Insight Agent
        </Typography>
        {batch.summary_model && (
          <Chip
            label={batch.summary_model}
            size="small"
            sx={{
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: '24px',
            }}
          />
        )}
        {batch.summary_execution_id && (
          <Button
            size="small"
            variant="text"
            startIcon={<ThumbUpIcon />}
            onClick={onFeedbackClick}
            sx={{
              color: theme.palette.primary.main,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 400,
              '&:hover': {
                backgroundColor: theme.palette.divider,
              },
            }}
          >
            Provide Feedback
          </Button>
        )}
      </Box>
      {/* Render markdown for template progression insights, plain text for operational */}
      {isProgression ? (
        <Box
          sx={{
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              color: theme.palette.text.primary,
              fontWeight: 600,
              marginTop: 2,
              marginBottom: 1,
            },
            '& h1': {
              fontSize: '1.5rem',
              borderBottom: `2px solid ${theme.palette.divider}`,
              paddingBottom: 1,
            },
            '& h2': {
              fontSize: '1.25rem',
              marginTop: 3,
            },
            '& h3': {
              fontSize: '1.125rem',
            },
            '& p': {
              color: theme.palette.text.primary,
              lineHeight: 1.7,
              fontSize: '0.9375rem',
              marginBottom: 1.5,
            },
            '& ul, & ol': {
              paddingLeft: 2,
              marginBottom: 1.5,
            },
            '& li': {
              color: theme.palette.text.primary,
              lineHeight: 1.7,
              fontSize: '0.9375rem',
              marginBottom: 0.5,
            },
            '& strong, & b': {
              fontWeight: 600,
              color: theme.palette.text.primary,
            },
            '& em, & i': {
              fontStyle: 'italic',
            },
            '& code': {
              backgroundColor: theme.palette.background.default,
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              color: theme.palette.text.primary,
            },
            '& pre': {
              backgroundColor: theme.palette.background.default,
              padding: 1.5,
              borderRadius: '8px',
              overflow: 'auto',
              marginBottom: 1.5,
            },
            '& pre code': {
              backgroundColor: 'transparent',
              padding: 0,
            },
            '& blockquote': {
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              paddingLeft: 2,
              marginLeft: 0,
              marginBottom: 1.5,
              fontStyle: 'italic',
              color: theme.palette.text.secondary,
            },
            '& hr': {
              border: 'none',
              borderTop: `1px solid ${theme.palette.divider}`,
              margin: '2rem 0',
            },
            '& a': {
              color: theme.palette.primary.main,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              marginBottom: 1.5,
            },
            '& th, & td': {
              border: `1px solid ${theme.palette.divider}`,
              padding: '8px 12px',
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: theme.palette.background.default,
              fontWeight: 600,
            },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {batch.summary}
          </ReactMarkdown>
        </Box>
      ) : (
        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'pre-wrap',
            lineHeight: 1.7,
            color: theme.palette.text.primary,
            fontSize: '0.9375rem',
            mb: 2,
          }}
        >
          {batch.summary}
        </Typography>
      )}
      {batch.summary_generated_at && (
        <Typography
          variant="caption"
          sx={{
            mt: 2,
            display: 'block',
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
          }}
        >
          Generated: {new Date(batch.summary_generated_at).toLocaleString()}
        </Typography>
      )}
    </CardContent>
  );
};

InsightSummaryCard.propTypes = {
  batch: PropTypes.object,
  batches: PropTypes.array.isRequired,
  isProgression: PropTypes.bool.isRequired,
  onFeedbackClick: PropTypes.func.isRequired,
  onViewPreviousReports: PropTypes.func,
};

export default InsightSummaryCard;
